import React from "react";
import { Avatar, Box, Button, Link, MenuItem } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { useBreadcrumbContext } from "../../../contexts/BreadcrumbProvider/BreadcrumbProvider";
import { IBreadcrumbProps } from "../../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { IStoreState } from "../../../redux/initialStoreState";
import { useDispatchWrapper, useRecordCounts } from "../../../hooks";
import {
  fetchNoteAsync,
  fetchNoteListAsync,
  fetchNoteListFromPolicyAsync,
} from "../../../redux/notes/notesActions";
import {
  IDataTableProps,
  MobileLogoRenderType,
  RenderType,
} from "../../../components/Table/interfaces/IDataTableProps";
import { LoadState, MODULE_IDS } from "../../../constants/enums";
import { INotices } from "../../../redux/notices/notices.types";
import { DataTable } from "../../../components/Table/DataTable";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { IPolicyNote } from "../../../redux/notes/notes.types";
import { StandadCard } from "../../../components/Cards";
import { Breadcrumb } from "../../../layout/fullLayout/breadcrumb/Breadcrumb";
import { useDateFilter } from "../../../components/Table/hooks/useDateFilter";
import { RoleBasedCSVExportPlugin } from "../../../security/RoleBasedComponents/RoleBasedCSVExportPlugin/RoleBasedCSVExportPlugin";
import { RefreshPlugin, SearchPlugin } from "../../../components/Table/plugins";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import { CustomSelect } from "../../../components/formsComponents";
import { DateFilterDropdown } from "../../../components/Table/components/DateFilterDropdown/DateFilterDropdown";
import { clearRecordCounts, fetchRecordCountsAsync } from "../../../redux/common/commonActions";
import { IconicMultiColumnCard } from "../../../components/iconicMultiColumnCard/IconicMultiColumnCard";
import { capitalizeWords } from "../../../helpers";
import { PageContainer } from "../../../components/container/PageContainer";

export const TaskNotesList: React.FC = () => {
  const { addBreadcrumb } = useBreadcrumbContext();
  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashboard/",
      title: "Dashboard",
    },
    {
      title: "Notes",
    },
  ];
  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) => storeState.policyNotes
  );
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const [tablePagination, setTablePagination] = React.useState({
    pageNumber: 1,
    rowsInPerPage: 25,
  });

  const { date, type, handleDateSelectChange, dateDropdownList, setDate } =
    useDateFilter({ defaultType: "allTimes" });

  const [searchType, setSearchType] = React.useState<string[]>(["notes_title"]);
  const [search, setSearch] = React.useState<string>("");
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "-1"
  );
  const { list: recordsCounts, loading: recordCountsLoading } =
  useRecordCounts();

  const fetchList = async () => {
    dispatch(
      fetchNoteListAsync(
        tablePagination.pageNumber,
        tablePagination.rowsInPerPage,
        status,
        date,
        search,
        searchType
      )
    );
  };

  React.useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePagination, date, search, status]);

  const handleRedirect = () => {
    navigate(`/manage-note`);
  };

  const handleEditRedirect=(customerPolicyId:  number, serialNumber: number) =>() => {
    navigate(`/manage-note/${customerPolicyId}/${serialNumber}`)
  }

  const notesTableProps: IDataTableProps = {
    isPagination: true,
    totalRecords: totalRecords,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    isDataLoading: loading === LoadState.InProgress,
    uniqueRowKeyName: "id",
    tableCommandBarProps: {
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={status}
                onChange={(e) => setStatus(e.target.value as "-1")}
              >
                <MenuItem value="-1">All Status</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Voided">Voided</MenuItem>
              </CustomSelect>
            ),
          },
          {
            key: "byDate",
            onRender: () => (
              <DateFilterDropdown
                value={type}
                options={dateDropdownList}
                onChange={handleDateSelectChange}
              />
            ),
          },
          {
            key: "fromDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.fromDate}
                  onChange={(newValue) => {
                    setDate({ ...date, fromDate: newValue });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "toDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.toDate}
                  onChange={(newValue) => {
                    setDate({ ...date, toDate: newValue });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "csvExport",
            onRender: (columns, items) => (
              <RoleBasedCSVExportPlugin
                columns={columns}
                items={items}
                filePrefixName="Report"
                moduleId={MODULE_IDS.NOTES}
              />
            ),
          },
          {
            key: "refresh",
            onRender: () => <RefreshPlugin onClick={fetchList} />,
          },
        ],
      },
      rightItems: {
        customPlugins: [
          {
            key: "search",
            onRender: () => {
              return (
                <SearchPlugin
                  selectedDropdownValue={searchType}
                  dropdownOptions={[
                    { label: "Customer Name", value: "customer" },
                    { label: "Title", value: "notes_title" },
                    { label: "Policy Number", value: "policy_number" },
                  ]}
                  onDropdownChange={(value) => setSearchType(value)}
                  onChange={(newValue) => {
                    setSearch(newValue);
                  }}
                />
              );
            },
          },
        ],
      },
    },
    mobileLogo: {
      type: MobileLogoRenderType.reactNode,
      onMobileLogoRender: (data: any) => {
        return (
          <Avatar
            sx={{
              width: 45,
              height: 45,
              color: "#fff",
              ml: "-8px",
              mr: 2,
            }}
          ></Avatar>
        );
      },
    },
    initialSortConfig: {
      "insert_ts": {
        direction: "desc",
        filedName: "insert_ts"
      }
    },
    columns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        isFirstColumnSticky: true,
        renderType: RenderType.CUSTOM_RENDER,
        headingAlign: "center",
        onRowCellRender: (value, row: IPolicyNote) => {
          return (
            <StandardTableActions
              commentBoxTypeId={
                row.customer_policy_id + "-" + row.incremental_number
              }
              onEditClick={handleEditRedirect(row.customer_policy_id,row.incremental_number as number)}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: RenderType.CUSTOM_RENDER,
        exportCellWidth: 20,
        onRowCellRender: (value, row) => {
          return <StatusRenderer status={row.status} />
        },
      },
      {
        key: "notes_title",
        headerName: "Title",
        fieldName: "notes_title",
        renderType: RenderType.CUSTOM_RENDER,
        exportCellWidth: 20,
        onRowCellRender: (value, row)=> {
          return    <Link
          sx={{ cursor: "pointer" }}
          onClick={handleEditRedirect(row.customer_policy_id,row.incremental_number as number)}
        >
          {row.notes_title}
        </Link>
        }
      },
      {
        key: "customer",
        headerName: "Customer",
        fieldName: "customer",
        renderType: RenderType.TEXT_DARK,
        exportCellWidth: 20,
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: RenderType.CHIP_WARNING,
        exportCellWidth: 30,
      },

      {
        key: "date_created",
        headerName: "Date",
        fieldName: "date_created",
        renderType: RenderType.DATE,
        exportCellWidth: 20,
      },
    
    ],
    items: list,
    onPageChange: (newPageNumber: number) => {
      setTablePagination({ ...tablePagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      setTablePagination({
        pageNumber: pageNumber,
        rowsInPerPage: rowsPerPage,
      });
    },
  };

  

  React.useEffect(() => {
    dispatch(
      fetchRecordCountsAsync(
        "latest_notes",
        "status",
        date.fromDate,
        date.toDate
      )
    );
    return () => {
      dispatch(clearRecordCounts());
    };
  }, [date.fromDate, date.toDate]);

  return (
    <PageContainer title="View Notes" description="this is innerpage">
      <Breadcrumb title="" items={BCrumb} />

      <IconicMultiColumnCard
        loading={recordCountsLoading !== LoadState.Loaded}
        columns={recordsCounts.map((item) => {
          return {
            heading: capitalizeWords(item.name),
            value: item.cnt,
            colSize: item.name.length > 10 ? 3: 2,
          };
        })}
      />

      <StandadCard
        heading="Notes"
        rightHeading={
          <Button variant="contained" onClick={handleRedirect}>
            Add Note
          </Button>
        }
      >
        <DataTable {...notesTableProps} />
      </StandadCard>
    </PageContainer>
  );
};
